// BestCaregiversSection.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus } from "lucide-react";
import careImage from "../assets/care-section-image.jpg";

const BestCaregiversSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const careServices = [
    {
      name: "Alzheimer's and Dementia Care",
      slug: "alzheimers-dementia-care",
      title: "Compassionate In-Home Senior Care For Seniors with Dementia",
      description:
        "We will sit down and customise a tailored dementia care program that works best for your family; one that delivers the individualised attention your loved one requires anywhere from hourly to continuous, 24-hour, monitoring. Our compassionate dementia caregivers stand ready to deliver the necessary patience, empathy, and nurturing to put your mind at ease and keep your loved one aging in place.",
    },
    {
      name: "Companionship",
      slug: "companionship-care",
      title: "Companionship Alleviating Loneliness",
      description:
        "We all need the company of other people. Seniors may struggle with loneliness from being alone. A caring and compassionate caregiver can provide them the social interaction and friendship that they need.",
    },
    {
      name: "Bathing, Grooming, and Dressing",
      slug: "personal-care",
      title: "Dignified In-Home Personal Care for Seniors",
      description:
        "Most of us wake up and start out our days using the bathroom, bathing, dressing and grabbing a bite to eat. For many seniors this daily routine is not so easy as they struggle to carry out some of these basic personal care activities. When it happens to your aging family member, you want them to remain at home while enjoying the independence and dignity they deserve.\n\nHaving a conversation with a senior loved one, especially a parent, about personal care can be embarrassing, and feelings oftentimes get hurt. To assist your efforts, Allawah Lodge offers reliable and discreet personal caregiving assistance for seniors.",
    },
    {
      name: "Hospice Care",
      slug: "hospice-care",
      title: "Hospice Care",
      description:
        "Hospice care provides compassionate support for seniors with life-limiting conditions and their families. The caregivers we refer focus on comfort, dignity, and quality of life, assisting with daily needs while providing emotional support. This approach allows seniors to spend their final days in the familiarity of home, surrounded by care and compassion.",
    },
    {
      name: "Specialty Care",
      slug: "specialty-care",
      title: "Specialty Care",
      description:
        "Our specialty care services are designed for seniors and adults with unique health needs or chronic conditions that require focused attention. The caregivers we match provide personalised support tailored to each individual, ensuring comfort, safety, and well-being. From managing complex medical routines to assisting with specialised equipment, our goal is to help clients maintain independence while receiving the care they need.",
    },
  ];

  return (
    <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Top Section */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            We Provide Only the Best Caregivers for Your Loved One
          </h2>

          <div className="space-y-4 text-gray-700 font-light leading-relaxed">
            <p>
              Every one of our caregivers is experienced, extensively trained,
              fully bonded and insured, and has completed comprehensive national
              background screenings. We only send individuals to your home whom
              we'd trust in our own, and each caregiver is thoughtfully paired
              with clients based on their unique needs and preferences. (For
              instance, we wouldn't match a talkative personality with someone
              who values tranquility.)
            </p>

            <p>
              Whether it's assistance with daily mobility or having a companion
              for games and conversation, our tailored and accessible in-home
              care ensures your loved one thrives at home — even when you're
              away. We deliver the kind of support that brings reassurance to
              families and happiness to those in our care.
            </p>
          </div>
        </div>

        {/* Bottom Section - Image and Services */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Image */}
          <div>
            <img
              src={careImage}
              alt="Caregiver with elderly client"
              className="w-full h-auto rounded-2xl object-cover"
            />
          </div>

          {/* Right Content */}
          <div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Right Care for Every Need
            </h3>

            <p className="text-gray-700 font-light mb-8 leading-relaxed">
              From companionship to personal care, the caregivers we match
              provide support tailored to every situation.
            </p>

            <div className="space-y-3">
              {careServices.map((service, index) => (
                <div key={index} className="border-b border-gray-200 pb-3">
                  <button
                    onClick={() =>
                      setOpenIndex(openIndex === index ? null : index)
                    }
                    className="w-full flex items-start gap-3 text-left hover:text-[#E85D2C] transition-colors"
                  >
                    {openIndex === index ? (
                      <Minus
                        size={20}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                    ) : (
                      <Plus
                        size={20}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                    )}
                    <span className="text-lg font-medium text-gray-800">
                      {service.name}
                    </span>
                  </button>

                  {openIndex === index && (
                    <div className="mt-4 ml-8 space-y-3">
                      {service.title && (
                        <h4 className="font-semibold text-gray-900">
                          {service.title}
                        </h4>
                      )}
                      <p className="text-gray-700 font-light leading-relaxed whitespace-pre-line">
                        {service.description}
                      </p>
                      <Link
                        to={`/services/${service.slug}`}
                        className="inline-block text-[#E85D2C] hover:text-[#d54d1f] font-semibold transition-colors"
                      >
                        Learn More...
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BestCaregiversSection;
